<template>
  <div id="contract-editor-asset-form" class="box">
    <b-container>
      <h2>{{ $t('ASSET_INFO') }}</h2>

      <b-row>
        <b-col xs="12" md="6" lg="4" class="input-wrapper" :class="{ locked: !!id }">
          <select-input
            :label="$t('ASSET_CATEGORY')"
            v-model="category"
            :items="categories"
            borderbottom
            required
            ref="categoryInput"
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper">
          <text-input
            :label="$t('DESCRIPTION')"
            v-model="description"
            borderbottom
            textarea
            required
            ref="descriptionInput"
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper" v-show="!isRealEstate">
          <text-input
            :label="$t('ACCESSORIES')"
            v-model="accessories"
            borderbottom
            textarea
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper">
          <text-input
            :label="$t('EXPECTED_AMOUNT')"
            v-model="expectedAmount"
            :hint="changeDigitToViText(expectedAmount)"
            borderbottom
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper">
          <text-input
            :label="$t('VALUATED_AMOUNT')"
            v-model="valuatedAmount"
            :hint="changeDigitToViText(valuatedAmount)"
            borderbottom
            required
            ref="valuatedAmountInput"
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper">
          <select-input
            :label="$t('VALUATOR')"
            v-model="valuator"
            :items="valuators"
            borderbottom
            required
            ref="valuatorInput"
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper" v-show="!isRealEstate">
          <text-input
            :label="$t('BRAND')"
            v-model="brand"
            borderbottom
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper" v-show="!isRealEstate">
          <text-input
            :label="$t('PRODUCT_LINE')"
            v-model="productLine"
            borderbottom
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper" v-show="isVehicle">
          <text-input
            :label="$t('CHASSIS_ID')"
            v-model="chassisId"
            borderbottom
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper" v-show="isVehicle">
          <text-input
            :label="$t('ENGINE_ID')"
            v-model="engineId"
            borderbottom
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper" v-show="isVehicle">
          <text-input
            :label="$t('COLOR')"
            v-model="color"
            borderbottom
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper" v-show="isVehicle">
          <text-input
            :label="$t('LICENSE_PLATE_ID')"
            v-model="licensePlateId"
            borderbottom
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper" v-show="isRealEstate">
          <text-input
            :label="$t('CO_OWNER')"
            v-model="coOwner"
            borderbottom
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper" v-show="isRealEstate">
          <text-input
            :label="$t('CO_OWNER_CITIZEN_ID')"
            v-model="coOwnerCitizenId"
            borderbottom
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper">
          <select-input
            :label="$t('STORAGE')"
            v-model="storage"
            :items="storages"
            borderbottom
            required
            ref="storageInput"
          />
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch, watchEffect } from '@vue/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';

import { changeDigitToViText } from '@/helpers';
import { MILLION } from '@/consts';

import TextInput from '@/components/TextInput';
import SelectInput from '@/components/SelectInput';

const { useState: useAppState, useActions: useAppActions } = createNamespacedHelpers('app');
const { useState: useUserState } = createNamespacedHelpers('user');

const VEHICLES = [
  'MOTORCYCLE',
  'AUTOMOBILE',
  'TRUCK'
];

const REAL_ESTATE = 'REAL_ESTATE';

export default {
  name: 'AssetForm',
  components: {
    TextInput,
    SelectInput
  },
  props: {
    value: {
      type: Object,
      default: () => {}
    }
  },
  setup(props, { root, emit }) {
    const { assetCategories, valuators: valuators_, storages: storages_ } = useAppState(['assetCategories', 'valuators', 'storages']);
    const { getValuators } = useAppActions(['getValuators']);
    const { branch: branchName } = useUserState(['branch']);

    const defaultStorage = computed(() => storages_.value.find((s) => (s.name === branchName.value)));

    const id = ref(null);
    const storage = ref(null);
    const category = ref(null);
    const description = ref('');
    const expectedAmount = ref('');
    const valuatedAmount = ref('');
    const valuator = ref(null);
    const meta = ref({});

    const storageInput = ref(null);
    const categoryInput = ref(null);
    const descriptionInput = ref(null);
    const valuatedAmountInput = ref(null);
    const valuatorInput = ref(null);

    const accessories = ref('');
    const brand = ref('');
    const productLine = ref('');
    const chassisId = ref('');
    const engineId = ref('');
    const color = ref('');
    const licensePlateId = ref('');
    const coOwner = ref('');
    const coOwnerCitizenId = ref('');

    const isVehicle = computed(() => {
      const cat = assetCategories.value.find((c) => c.id === category.value);
      if (!cat) return false;

      return (VEHICLES.indexOf(cat.key) >= 0);
    });

    const isRealEstate = computed(() => {
      const cat = assetCategories.value.find((c) => c.id === category.value);
      if (!cat) return false;

      return (cat.key === REAL_ESTATE);
    });

    const wrappedAsset = computed(() => ({
      id: id.value || undefined,
      categoryId: category.value,
      storageId: storage.value,
      description: description.value,
      expectedAmount: expectedAmount.value ? Math.floor(parseFloat(expectedAmount.value) * MILLION) : undefined,
      valuatedAmount: valuatedAmount.value ? Math.floor(parseFloat(valuatedAmount.value) * MILLION) : null,
      valuatedById: valuator.value,
      meta: {
        accessories: accessories.value || undefined,
        brand: brand.value || undefined,
        productLine: productLine.value || undefined,
        chassisId: chassisId.value || undefined,
        engineId: engineId.value || undefined,
        color: color.value || undefined,
        licensePlateId: licensePlateId.value || undefined,
        coOwner: coOwner.value || undefined,
        coOwnerCitizenId: coOwnerCitizenId.value || undefined
      },
      isVehicle: isVehicle.value || false,
      isRealEstate: isRealEstate.value || false
    }));

    const categories = computed(() => assetCategories.value.map((item) => ({
      text: root.$t(item.key),
      value: item.id
    })));

    const valuators = computed(() => valuators_.value?.map((item) => ({
      text: `${item.firstName} ${item.lastName} (${item.username})`,
      value: item.id
    })));

    const storages = computed(() => storages_.value?.map((item) => ({
      text: item.name,
      value: item.id
    })));

    const submit = () => {
      const inputs = [
        categoryInput.value,
        descriptionInput.value,
        valuatedAmountInput.value,
        valuatorInput.value
      ];

      let valid = true;

      inputs.forEach((input) => {
        const v = input.validateInput();
        valid = valid && v;
      });

      return valid;
    };

    const loadAssetInfo = (val) => {
      if (val) {
        id.value = val.id || null;
        category.value = val.categoryId || null;
        storage.value = val.storageId || null;
        description.value = val.description || '';
        expectedAmount.value = val.expectedAmount ? (val.expectedAmount / MILLION).toFixed(3) : '';
        valuatedAmount.value = val.valuatedAmount ? (val.valuatedAmount / MILLION).toFixed(3) : '';
        valuator.value = val.valuatedById || null;
        meta.value = val.meta || {};
      }
    };

    watchEffect(() => {
      emit('input', wrappedAsset.value);
    });

    watch(meta, (val) => {
      accessories.value = val.accessories || '';
      brand.value = val.brand || '';
      productLine.value = val.productLine || '';
      chassisId.value = val.chassisId || '';
      engineId.value = val.engineId || '';
      color.value = val.color || '';
      licensePlateId.value = val.licensePlateId || '';
      coOwner.value = val.coOwner || '';
      coOwnerCitizenId.value = val.coOwnerCitizenId || '';
    });

    watch(defaultStorage, (val) => {
      storage.value = val?.id || null;
    }, {
      immediate: true
    });

    onMounted(() => {
      getValuators();
    });

    return {
      category,
      categories,
      description,
      accessories,
      expectedAmount,
      valuatedAmount,
      changeDigitToViText,
      valuator,
      valuators,
      isVehicle,
      brand,
      productLine,
      chassisId,
      engineId,
      color,
      licensePlateId,
      coOwner,
      coOwnerCitizenId,
      isRealEstate,
      categoryInput,
      descriptionInput,
      valuatedAmountInput,
      valuatorInput,
      submit,
      loadAssetInfo,
      storage,
      storages,
      storageInput,
      id
    };
  }
};
</script>

<style lang="scss">
@import "~@/assets/scss/colors";
</style>
